import { BaseSelectorOption } from '@/types';
import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  Chip,
  createFilterOptions,
  Stack,
} from '@mui/material';
import { Component, createRef } from 'react';
import DropIcon from '../../resources/images/icons-chevron-double.svg';
import RemoveIcon from '../../resources/images/icons-remove.svg';
import { TextField } from '../TextField';
import { MultiSelectorContent } from './MultiSelectorContent';

interface GroupByMultiSelectorProps<T extends BaseSelectorOption = BaseSelectorOption> {
  options: T[];
  value: T[];
  onChange?: (e: any, values: T[]) => void;
  getOptionLabel?: (option: T) => string;
  getOptionId?: (option: T) => string;
  validationMethod?: (e: any, value: T[]) => boolean;
  onBlur?: (e: any) => void;
  onSearch?: (value: string) => void;
  isValidateOnMount?: boolean;
  validationText?: string;
  helperText?: string;
  className?: string;
  style?: React.CSSProperties;
  isShowValue?: boolean;
  label?: string;
  placeHolder?: string;
  disabled?: boolean;
  groupBy?: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  noOptionsText?: string;
  translate?: (key: string, options?: Record<string, unknown>) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  getOptionDisabled?: (option: T) => boolean;
  isLoading?: boolean;
}

interface GroupByMultiSelectorState {
  valid: boolean | null;
  searchText: string;
  selectorContainerSize: any;
}

export class GroupByMultiSelector<
  T extends BaseSelectorOption = BaseSelectorOption,
> extends Component<GroupByMultiSelectorProps<T>, GroupByMultiSelectorState> {
  selectorContainerRef: any;
  filterOptions: any;
  static defaultProps = {
    isShowValue: true,
  };
  constructor(props: any) {
    super(props);

    this.state = {
      valid: null,
      searchText: '',
      selectorContainerSize: null,
    };

    this.selectorContainerRef = createRef();

    this.filterOptions = createFilterOptions({
      matchFrom: 'any',
      stringify: (option: any) => this.props.getOptionLabel?.(option) ?? option?.label,
    });
  }

  onGetSelectorContainerSize() {
    const selectorContainerSize = this.selectorContainerRef?.current.getBoundingClientRect();
    this.setState({ selectorContainerSize });
  }

  validate(e: any) {
    const { validationMethod } = this.props;
    const { valid } = this.state;

    if (validationMethod) {
      const newValid = validationMethod(e, e ? e.target.value : null);
      if (newValid !== valid) this.setState({ valid: newValid });
    }
  }

  onInputChange(e: any) {
    const { onSearch } = this.props;

    if (e && onSearch) onSearch(e.target.value);
  }

  onBlur(e: any) {
    const { onBlur } = this.props;

    this.validate(e);

    if (onBlur) onBlur(e);
  }

  onRemoveItem(e: any, item: any) {
    const { onChange, value, getOptionId } = this.props;

    const newValues = value.filter((i) => getOptionId?.(i) !== getOptionId?.(item));

    onChange?.(e, newValues);
  }

  renderOption(props: any, option: any, state: any) {
    const { selectorContainerSize } = this.state;

    return (
      <MultiSelectorContent
        option={option}
        inputValue={state.inputValue}
        props={props}
        selectorContainerSize={selectorContainerSize}
        getItemLabel={this.props.getOptionLabel}
        getItemId={this.props.getOptionId}
      />
    );
  }

  componentDidMount() {
    const { isValidateOnMount } = this.props;

    this.onGetSelectorContainerSize();

    if (isValidateOnMount) {
      this.validate(null);
    }
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps.value !== this.props.value) {
      this.validate(null);
    }
  }

  render() {
    const { valid } = this.state;
    const {
      label,
      placeHolder,
      validationText,
      helperText,
      className,
      isShowValue,
      style,
      onSearch,
      isValidateOnMount,
      validationMethod,
      getOptionId,
      getOptionLabel,
      options,
      noOptionsText,
      translate,
      renderGroup,
      isLoading,
      ...autocompleteProps
    } = this.props;
    const { value, disabled } = autocompleteProps;

    // Use translated noOptionsText if translate function is available
    const effectiveNoOptionsText =
      noOptionsText || translate?.('common.noSearchResults') || 'No search results';

    return (
      <Stack
        className={`klayo-multiselector ${className ?? ''}`}
        direction='column'
        spacing={2}
        ref={this.selectorContainerRef}
      >
        <Autocomplete
          className='klayo-selector'
          options={options}
          autoComplete
          includeInputInList
          filterSelectedOptions
          popupIcon={<DropIcon />}
          clearIcon={<RemoveIcon />}
          disableClearable={true}
          handleHomeEndKeys
          noOptionsText={effectiveNoOptionsText}
          renderGroup={renderGroup}
          loading={isLoading}
          ListboxProps={{
            className: 'klayo-multiselector_dropdownlist group-by',
            style: { ...style },
          }}
          ChipProps={{
            deleteIcon: <RemoveIcon />,
          }}
          multiple
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label={label}
                placeholder={placeHolder ? placeHolder : 'Search'}
                error={valid === false}
                helperText={valid === false ? validationText : helperText}
                endAdornment={params.InputProps.endAdornment}
                value={this.state.searchText}
                onChange={this.onInputChange.bind(this)}
                InputProps={{
                  ...params.InputProps,
                  onKeyDown: (e: any) => {
                    if (e.key === 'Backspace' && this.state.searchText === '') {
                      e.stopPropagation();
                      return;
                    }
                  },
                }}
              />
            );
          }}
          filterOptions={this.filterOptions}
          // onInputChange={this.onInputChange.bind(this)}
          renderOption={this.renderOption.bind(this)}
          getOptionLabel={getOptionLabel}
          {...autocompleteProps}
        />
        {isShowValue && (
          <Stack
            className={`klaro-chiparray ${value.length ? 'has-selected' : ''}`}
            direction='row'
            spacing={1}
            sx={{ flexWrap: 'wrap' }}
          >
            {value.map((option: any) => {
              return (
                <Chip
                  key={getOptionId?.(option)}
                  label={getOptionLabel?.(option)}
                  onDelete={(e) => this.onRemoveItem(e, option)}
                  deleteIcon={<RemoveIcon />}
                  disabled={disabled}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  }
}
